import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Upload, Download, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
const StatementGeneratorPage = () => {
  const [name, setName] = useState('');
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedFlyer, setGeneratedFlyer] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = event => {
        setUploadedImage(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  const generateFlyer = async () => {
    if (!name.trim()) {
      toast.error('Please enter your name first!');
      return;
    }
    if (!uploadedImage) {
      toast.error('Please upload an image first!');
      return;
    }
    setIsGenerating(true);
    try {
      await createFlyer();
      toast.success('Flyer generated successfully!');
    } catch (error) {
      toast.error('Failed to generate flyer');
    } finally {
      setIsGenerating(false);
    }
  };
  const createFlyer = () => {
    return new Promise<void>(resolve => {
      const canvas = canvasRef.current;
      if (!canvas) return resolve();
      const ctx = canvas.getContext('2d');
      if (!ctx) return resolve();

      // Set canvas size for trendy social media format
      canvas.width = 1080;
      canvas.height = 1080;

      // Create gradient background (Gen Z vibes)
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, '#ff6b6b');
      gradient.addColorStop(0.3, '#4ecdc4');
      gradient.addColorStop(0.7, '#45b7d1');
      gradient.addColorStop(1, '#96ceb4');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Add some trendy shapes
      ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
      ctx.beginPath();
      ctx.arc(900, 200, 150, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(200, 900, 100, 0, Math.PI * 2);
      ctx.fill();

      // Load and draw user image
      const img = new Image();
      img.onload = () => {
        // Create circular mask for profile picture
        const centerX = canvas.width / 2;
        const centerY = 350;
        const radius = 140;
        ctx.save();
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.clip();

        // Draw image to fit in circle
        const imgSize = Math.min(img.width, img.height);
        const sx = (img.width - imgSize) / 2;
        const sy = (img.height - imgSize) / 2;
        ctx.drawImage(img, sx, sy, imgSize, imgSize, centerX - radius, centerY - radius, radius * 2, radius * 2);
        ctx.restore();

        // Add white ring around profile
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 8;
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius + 4, 0, Math.PI * 2);
        ctx.stroke();

        // Add main text
        ctx.textAlign = 'center';
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 72px Arial, sans-serif';
        ctx.fillText(`I AM ${name.toUpperCase()}`, centerX, 600);
        ctx.font = 'bold 56px Arial, sans-serif';
        ctx.fillText('I SAY NO TO', centerX, 680);
        ctx.font = 'bold 60px Arial, sans-serif';
        ctx.fillText('INDIGENOUS BILL', centerX, 740);

        // Add some emoji/sparkles
        ctx.font = '48px Arial, sans-serif';
        ctx.fillText('✊🏾 🚫 📜', centerX, 820);

        // Add trendy hashtags
        ctx.font = '32px Arial, sans-serif';
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.fillText('#NoToIndigenousBill #StandUp #OneVoice', centerX, 900);

        // Add footer with deep link
        ctx.font = '24px Arial, sans-serif';
        ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
        ctx.fillText('Want your friends to create their own flyer?', centerX, 960);
        ctx.font = 'bold 28px Arial, sans-serif';
        ctx.fillStyle = '#ffffff';
        ctx.fillText('🔗 Generate Your Statement Flyer', centerX, 1000);
        ctx.font = '22px Arial, sans-serif';
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.fillText('https://tinyurl.com/bdcrzsxh', centerX, 1040);

        // Convert to blob and create download URL
        canvas.toBlob(blob => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            setGeneratedFlyer(url);
          }
          resolve();
        });
      };
      img.src = uploadedImage!;
    });
  };
  const downloadFlyer = () => {
    if (generatedFlyer) {
      const link = document.createElement('a');
      link.download = `${name.toLowerCase()}-statement-flyer.png`;
      link.href = generatedFlyer;
      link.click();
    }
  };
  const resetForm = () => {
    setName('');
    setUploadedImage(null);
    setGeneratedFlyer(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  return <div className="min-h-screen bg-gradient-subtle px-4 py-8">
      <div className="container mx-auto max-w-2xl">
        <Card className="shadow-elegant">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-bold text-foreground">
              Statement Flyer Generator ✊🏾
            </CardTitle>
            <CardDescription className="text-lg">
              Create a trendy flyer with your photo and statement against the Indigenous Bill
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name Input */}
              <div className="space-y-2">
                <Label htmlFor="nameInput" className="text-base font-semibold">
                  Your Name:
                </Label>
                <Input id="nameInput" type="text" placeholder="e.g., Bayo" value={name} onChange={e => setName(e.target.value)} className="text-lg p-4 h-auto" autoComplete="off" autoCapitalize="words" />
              </div>

              {/* Image Upload */}
              <div className="space-y-2">
                <Label htmlFor="imageInput" className="text-base font-semibold">
                  Your Photo:
                </Label>
                <div className="relative">
                  <Input ref={fileInputRef} id="imageInput" type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                  <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} className="w-full p-4 h-auto text-base">
                    <Upload className="w-5 h-5 mr-2" />
                    {uploadedImage ? 'Change Photo' : 'Upload Photo'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Image Preview */}
            {uploadedImage && <div className="flex justify-center">
                <img src={uploadedImage} alt="Preview" className="w-32 h-32 object-cover rounded-full border-4 border-primary shadow-lg" />
              </div>}

            {/* Generate Button */}
            <Button onClick={generateFlyer} disabled={isGenerating || !name.trim() || !uploadedImage} className="w-full text-lg py-6 h-auto transition-smooth" size="lg">
              {isGenerating ? <>
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  Creating Your Flyer...
                </> : 'Generate Statement Flyer 🔥'}
            </Button>

            {/* Generated Flyer Preview */}
            {generatedFlyer && <Card className="bg-muted/50 border-l-4 border-l-primary">
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <div className="text-center">
                      <h3 className="text-xl font-bold mb-4">Your Statement Flyer</h3>
                      <div className="inline-block border-4 border-primary rounded-lg overflow-hidden shadow-lg">
                        <img src={generatedFlyer} alt="Generated Flyer" className="w-full max-w-md" />
                      </div>
                     </div>
                     
                     {/* Download Button - Centered */}
                     <div className="text-center my-6">
                       <Button onClick={downloadFlyer} className="text-base py-4 px-8 h-auto">
                         <Download className="w-4 h-4 mr-2" />
                         Download Flyer
                       </Button>
                     </div>
                     
                     <div className="text-center my-6">
                       <p className="text-lg font-semibold text-foreground mb-4">
                         Now it's your turn to share with friends and family 🙏
                       </p>
                       
                       <div className="mb-6 p-4 bg-muted/30 rounded-lg text-left">
                         <p className="text-sm font-medium mb-3 text-foreground">⚠️ Consequences of the So-Called 'Indigeneship Bill':</p>
                         <div className="space-y-1 text-sm text-muted-foreground">
                           <p className="text-zinc-950">❌ Loss of indigenous land and heritage</p>
                           <p className="text-zinc-950">❌ Future generations stripped of ancestral rights</p>
                           <p className="text-zinc-950">❌ Weakening of traditional institutions </p>
                           <p className="text-zinc-950">✅ Rising identity clashes and inter-ethnic tensions</p>
                           <p className="text-zinc-950">✅ Increased political instability</p>
                           <p className="text-zinc-950">✅ Security threats worsened by Insurgent and successionist groups</p>
                         </div>
                         <div className="mt-4 space-y-2 text-sm text-foreground">
                           <p className="font-medium text-zinc-950">Our strength lies in our heritage, peace, and unity.</p>
                           <p className="text-zinc-950">Don't let it be erased in the name of false inclusion.</p>
                           <p className="font-bold text-zinc-950">🚫 Say NO to the Evil Indigeneship Bill</p>
                           <p className="italic text-zinc-950">It is an expansionist agenda in disguise.</p>
                         </div>
                       </div>
                       
                        <div className="flex flex-wrap justify-center gap-2 mb-4">
                          <Button onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank')} variant="outline" size="sm" className="text-blue-600 border-blue-200 hover:bg-blue-50">
                            Facebook
                          </Button>
                          <Button onClick={() => window.open(`https://www.tiktok.com/`, '_blank')} variant="outline" size="sm" className="text-gray-900 border-gray-200 hover:bg-gray-50">
                            TikTok
                          </Button>
                          <Button onClick={() => window.open(`https://twitter.com/intent/tweet?text=I%20am%20${encodeURIComponent(name)}%20and%20I%20say%20No%20to%20Indigenous%20Bill%20%23NoToIndigenousBill%20%23onevoice%20Generate%20yours:%20https://www.omooduduwa.org/statement-generator`, '_blank')} variant="outline" size="sm" className="text-blue-400 border-blue-200 hover:bg-blue-50">
                            Twitter
                          </Button>
                          <Button onClick={() => window.open(`https://www.instagram.com/`, '_blank')} variant="outline" size="sm" className="text-pink-600 border-pink-200 hover:bg-pink-50">
                            Instagram
                          </Button>
                          <Button onClick={() => window.open(`https://wa.me/?text=I%20am%20${encodeURIComponent(name)}%20and%20I%20say%20No%20to%20Indigenous%20Bill%20%23NoToIndigenousBill%20%23onevoice%0A%0AGenerate%20your%20own%20statement%20flyer:%20https://www.omooduduwa.org/statement-generator`, '_blank')} variant="outline" size="sm" className="text-green-600 border-green-200 hover:bg-green-50">
                            WhatsApp
                          </Button>
                        </div>
                        
                        {/* Deep Link Call to Action */}
                        <div className="mt-6 p-4 bg-primary/10 border border-primary/20 rounded-lg">
                          <div className="text-center space-y-3">
                            <p className="text-base font-semibold text-foreground">
                              ✨ Want your friends to create their own flyer?
                            </p>
                            <p className="text-sm text-muted-foreground">
                              Share this link so they can generate their personalized statement flyer
                            </p>
                            <Button 
                              onClick={() => window.open('https://www.omooduduwa.org/statement-generator', '_blank')} 
                              className="text-base py-3 px-6 h-auto bg-primary hover:bg-primary/90"
                            >
                              🔗 Generate Your Statement Flyer
                            </Button>
                          </div>
                        </div>
                     </div>
                     
                     <div className="text-center">
                       <Button onClick={resetForm} variant="outline" className="text-base py-4 px-8 h-auto">
                         <RefreshCw className="w-4 h-4 mr-2" />
                         Create Another
                       </Button>
                     </div>
                  </div>
                </CardContent>
              </Card>}
          </CardContent>
        </Card>

        {/* Hidden Canvas */}
        <canvas ref={canvasRef} style={{
        display: 'none'
      }} />
      </div>
    </div>;
};
export default StatementGeneratorPage;